import classes from "./EquipmentPage.module.css";
import React, { useEffect, useState } from "react";
import {
    createNewEquipment,
    deleteEquipment,
    editEquipment,
    getEquipment,
    getEquipments,
} from "../../api/equipmentAPI";
import { EquipmentData, EquipmentDataCreate, EquipmentDataEdit } from "../../types/api/equipment";
import { SubmitHandler, useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import Modal from "../../components/UI/Modal/Modal";
import {
    Button,
    Pagination,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from "@mui/material";
import Loader from "../../components/Loader/Loader";
import { MODAL_TYPE } from "../../constants/common";
import { useNotification } from "../../hooks/useNotification";
import CustomSnackbar from "../../components/UI/CustomSnackbar";
import Search from "../../components/Search/Search";
import CreateContent from "./Modals/CreateContent";
import EditContent from "./Modals/EditContent";
import { BASE_IMG_URL } from "../../constants/mainAPI";

const EquipmentPage = () => {
    const { id } = useParams();
    const isOwner =
        localStorage.getItem("employee_type") == "16" &&
        localStorage.getItem("employee_center_id") == localStorage.getItem("current_center_id");
    const [load, setLoad] = useState(false);
    const [pagination, setPagination] = useState({
        current_page: 1,
        last_page: 1,
    });
    const [equipments, setEquipments] = useState<Array<EquipmentData>>();
    const [equipmentId, setEquipmentId] = useState(0);
    const [deleteEquipmentId, setDeleteEquipmentId] = useState(0);
    const [query, setQuery] = useState("");
    const { setNotification, setOpenSnackbar, notification, openSnackbar } = useNotification();
    const [showModal, setShowModal] = useState<boolean>(false);
    const [currentModalType, setCurrentModalType] = useState("edit");
    const closeModal = () => {
        setShowModal(false);
    };

    const fetchEquipments = (query?: string) => {
        setLoad(true);
        getEquipments(
            id,
            ({ data, last_page, current_page }) => {
                setEquipments(data);
                setPagination({ ...pagination, last_page: last_page, current_page: current_page });
                setLoad(false);
            },
            err => {
                setNotification({
                    severity: "error",
                    message: err.message || "",
                });
                setLoad(false);
            },
            query ? { page: pagination.current_page, q: query } : { page: pagination.current_page },
        );
    };

    const editResetFormData = (data: EquipmentData) => {
        editForm.reset({
            ...data,
            cost: data.cost ? data.cost / 100 : data.cost,
            start_date: data.start_date?.slice(0, 10).replace(/-/g, "/"),
            date: data.date?.slice(0, 10).replace(/-/g, "/"),
        });
    };

    const getEquipmentData = (equipmentId: number) => {
        setLoad(true);
        getEquipment(
            id,
            equipmentId,
            ({ data }) => {
                setLoad(false);
                setShowModal(true);
                editResetFormData(data);
            },
            err => {
                setLoad(false);
                setNotification({
                    severity: "error",
                    message: err.message || "",
                });
            },
        );
    };

    const createForm = useForm<EquipmentDataCreate>();
    const editForm = useForm<EquipmentDataEdit>();

    const create: SubmitHandler<EquipmentDataCreate> = (data: any) => {
        setLoad(true);
        createNewEquipment(
            id,
            { ...data, cost: data.cost ? data.cost * 100 : 0 },
            () => {
                fetchEquipments(query);
                setNotification({
                    severity: "success",
                    message: "Оборудование добавлено успешно",
                });
                setOpenSnackbar(true);
                setLoad(false);
                closeModal();
            },
            err => {
                setNotification({
                    severity: "error",
                    message: err.message || "",
                });
                setOpenSnackbar(true);
                setLoad(false);
                Object.keys(err.errors).forEach((fieldName: any) => {
                    createForm.setError(fieldName, {
                        type: "custom",
                        message: err.errors[fieldName][0],
                    });
                });
            },
        );
    };
    const edit: SubmitHandler<EquipmentDataCreate> = (data: any) => {
        setLoad(true);
        editEquipment(
            id,
            equipmentId,
            { ...data, cost: data.cost ? data.cost * 100 : 0 },
            ({ data }) => {
                editResetFormData(data);
                fetchEquipments(query);
                setNotification({
                    severity: "success",
                    message: "Оборудование изменено успешно",
                });
                setOpenSnackbar(true);
                setLoad(false);
                closeModal();
            },
            err => {
                setNotification({
                    severity: "error",
                    message: err.message || "",
                });
                setLoad(false);
                setOpenSnackbar(true);
                Object.keys(err.errors).forEach((fieldName: any) => {
                    editForm.setError(fieldName, {
                        type: "custom",
                        message: err.errors[fieldName][0],
                    });
                });
            },
        );
    };
    const del: SubmitHandler<any> = () => {
        setLoad(true);
        deleteEquipment(
            id,
            deleteEquipmentId,
            () => {
                fetchEquipments(query);
                setNotification({
                    severity: "success",
                    message: "Оборудование удалено успешно",
                });
                setOpenSnackbar(true);
                setLoad(false);
            },
            err => {
                setNotification({
                    severity: "error",
                    message: err.message || "",
                });
                setOpenSnackbar(true);
                setLoad(false);
            },
        );
        closeModal();
    };

    const deleteContent = <div>Вы уверены, что хотите удалить данные о оборудовании?</div>;

    const modalType = {
        [MODAL_TYPE.create]: {
            title: "Создание оборудования",
            onSubmit: createForm.handleSubmit(create),
            content: <CreateContent createForm={createForm} />,
        },
        [MODAL_TYPE.edit]: {
            title: "Редактирование оборудования",
            onSubmit: editForm.handleSubmit(edit),
            content: <EditContent form={editForm} />,
        },
        [MODAL_TYPE.delete]: {
            title: "Удаление оборудования",
            onSubmit: del,
            content: deleteContent,
        },
    };

    useEffect(() => {
        setLoad(true);
        fetchEquipments(query);
    }, [pagination.current_page, query]);

    useEffect(() => {
        equipmentId && getEquipmentData(equipmentId);
    }, [equipmentId]);

    return (
        <div className={"container"}>
            <div className={"pageHeader"}>Оборудование</div>
            <Search
                clearHandler={value => {
                    setQuery(value);
                }}
                searchHandler={value => {
                    setQuery(value);
                }}
            />
            <TableContainer component={Paper} sx={{ marginBottom: "80px" }}>
                <Table aria-label="equipment table" sx={{ "& td": { border: "none" } }}>
                    <TableHead sx={{ "& th": { fontSize: "16px", fontWeight: 700 } }}>
                        <TableRow>
                            <TableCell>Наименование</TableCell>
                            <TableCell align="center">Фото</TableCell>
                            <TableCell align="center">Год выпуска</TableCell>
                            <TableCell align="center">Ввод в эксплуатацию</TableCell>
                            <TableCell align="center">Закупочная стоимость</TableCell>
                            <TableCell align="center"></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {equipments?.map(equipment => (
                            <TableRow key={equipment.id} sx={{ border: 0 }}>
                                <TableCell sx={{ maxWidth: "250px", wordWrap: "break-word" }}>
                                    {equipment.name}
                                </TableCell>
                                <TableCell align="center">
                                    <img
                                        alt={equipment.photos[0]?.name}
                                        className={classes.equipment__photo}
                                        src={`${BASE_IMG_URL}/${equipment.photos[0]?.path}`}
                                    />
                                </TableCell>
                                <TableCell align="center">{String(equipment.date).slice(0, 10)}</TableCell>
                                <TableCell align="center">{String(equipment.start_date).slice(0, 10)}</TableCell>
                                <TableCell align="center">{equipment.cost / 100} &#8381;</TableCell>
                                <TableCell align="center">
                                    {isOwner && (
                                        <div className={"btnContainer"}>
                                            <Button
                                                variant="outlined"
                                                fullWidth
                                                onClick={() => {
                                                    equipmentId === equipment.id && setShowModal(true);
                                                    setEquipmentId(equipment.id);
                                                    setCurrentModalType(MODAL_TYPE.edit);
                                                }}>
                                                Изменить
                                            </Button>
                                            <Button
                                                fullWidth
                                                variant="outlined"
                                                onClick={() => {
                                                    setShowModal(true);
                                                    setDeleteEquipmentId(equipment.id);
                                                    setCurrentModalType(MODAL_TYPE.delete);
                                                }}>
                                                Удалить
                                            </Button>
                                        </div>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <div className={"posBR"}>
                <div className={"paginationContainer"}>
                    <Pagination
                        onChange={(index, qwerty) => setPagination({ ...pagination, current_page: qwerty })}
                        page={pagination.current_page}
                        count={pagination.last_page}
                        shape="rounded"
                    />
                </div>
                {isOwner && (
                    <Button
                        variant="outlined"
                        onClick={() => {
                            createForm.reset();
                            setCurrentModalType(MODAL_TYPE.create);
                            setShowModal(true);
                        }}>
                        Добавить
                    </Button>
                )}
            </div>
            <Modal
                open={showModal}
                title={modalType[currentModalType]?.title}
                onClose={() => {
                    closeModal();
                }}
                onSubmit={modalType[currentModalType]?.onSubmit}>
                {modalType[currentModalType]?.content}
            </Modal>
            <CustomSnackbar openSnackbar={openSnackbar} setOpenSnackbar={setOpenSnackbar} notification={notification} />
            <Loader backDrop open={load} />
        </div>
    );
};

export default EquipmentPage;
